import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { QrCode, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import type { Channel } from '@conversia/shared-types';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { useChannelsStore } from '@/store/channels.store';
import * as channelsApi from '@/api/channels.api';

/** Cada cuánto se pide el QR mientras no llega (ms). */
const POLL_MS = 2_000;
/** Pasado este lapso sin QR, se ofrece reintentar en vez de esperar a ciegas (ms). */
const ESPERA_MAX_MS = 25_000;

/** Tras conectar, el modal se cierra solo pasado este lapso (ms). */
const AUTO_CLOSE_MS = 1_800;

/**
 * Muestra el QR de vinculación de WhatsApp. El QR y el estado en vivo llegan por
 * WebSocket (`channel.status` → store). Cuando el canal pasa a CONNECTED el modal
 * muestra el éxito y se cierra solo; siempre hay un botón "Cerrar" disponible.
 */
export function QrModal({
  channel,
  open,
  onClose,
  onRetry,
}: {
  channel: Channel | null;
  open: boolean;
  onClose: () => void;
  /** Vuelve a pedir la conexión (nuevo QR). */
  onRetry?: () => void;
}) {
  const qr = useChannelsStore((s) => (channel ? s.qrByChannel[channel.id] : undefined));
  const error = useChannelsStore((s) => (channel ? s.errorByChannel[channel.id] : undefined));
  const status = useChannelsStore((s) => (channel ? s.statusByChannel[channel.id] : undefined));
  const isImage = qr?.startsWith('data:image');
  const isConnected = status === 'CONNECTED';
  const isSyncing = status === 'CONNECTING' || status === 'RECONNECTING';
  const setStatus = useChannelsStore((s) => s.setStatus);

  // El QR se PIDE además de esperarlo por WebSocket: el aviso en vivo se pierde
  // si la ventana se abre después de emitido, y entonces había que esperar al
  // siguiente QR (o para siempre). Se consulta al abrir y cada 2 s hasta que
  // haya QR o el canal quede conectado.
  // Se deja de pedir en cuanto hay QR (imagen o texto) o el canal conectó.
  const pedir = open && Boolean(channel) && !isConnected && !qr;
  const { data: vivo } = useQuery({
    queryKey: ['channel-qr', channel?.id],
    queryFn: () => channelsApi.getChannelQr(channel!.id),
    enabled: pedir,
    refetchInterval: pedir ? POLL_MS : false,
  });
  useEffect(() => {
    if (channel && vivo && (vivo.qr || vivo.status === 'CONNECTED')) {
      setStatus(channel.id, vivo.status, vivo.qr ?? undefined);
    }
  }, [channel, vivo, setStatus]);

  // Si pasa demasiado sin QR, se dice y se ofrece reintentar: nunca una espera muda.
  const [demora, setDemora] = useState(false);
  useEffect(() => {
    setDemora(false);
    if (!pedir) return;
    const t = setTimeout(() => setDemora(true), ESPERA_MAX_MS);
    return () => clearTimeout(t);
  }, [pedir, channel?.id]);

  // Al conectar, cerrar el modal automáticamente tras un breve éxito visible.
  useEffect(() => {
    if (!open || !isConnected) return;
    const t = setTimeout(onClose, AUTO_CLOSE_MS);
    return () => clearTimeout(t);
  }, [open, isConnected, onClose]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Conectar ${channel?.name ?? 'canal'}`}
      description="Abre WhatsApp › Dispositivos vinculados › Vincular dispositivo y escanea el código."
      size="sm"
      footer={
        <div className="flex gap-2">
          {!isConnected && onRetry && (demora || error) && (
            <Button variant="outline" onClick={onRetry}>
              Reintentar
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            {isConnected ? 'Listo' : 'Cerrar'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col items-center gap-4 py-2">
        <div className="flex h-56 w-56 items-center justify-center rounded-lg border border-surface-border bg-white p-3 dark:border-slate-700">
          {error ? (
            <div className="flex flex-col items-center gap-3 px-2 text-center text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-16 w-16" strokeWidth={1} />
              <span className="text-xs">{error}</span>
            </div>
          ) : isConnected ? (
            <div className="flex flex-col items-center gap-3 text-center text-green-600 dark:text-green-400">
              <CheckCircle2 className="h-16 w-16" strokeWidth={1.5} />
              <span className="text-sm font-medium">¡Conectado!</span>
            </div>
          ) : isImage ? (
            <img src={qr} alt="QR de conexión" className="h-full w-full object-contain" />
          ) : qr ? (
            <pre className="max-h-full overflow-auto whitespace-pre-wrap break-all text-[6px] leading-[7px] text-slate-700">
              {qr}
            </pre>
          ) : isSyncing ? (
            <div className="flex flex-col items-center gap-3 text-slate-400">
              <Loader2 className="h-16 w-16 animate-spin" strokeWidth={1} />
              <span className="text-xs">Sincronizando WhatsApp…</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 text-slate-300">
              <QrCode className="h-20 w-20" strokeWidth={1} />
              {demora ? (
                <span className="px-2 text-center text-xs text-amber-600 dark:text-amber-400">
                  El código está tardando. Reintenta la conexión.
                </span>
              ) : (
                <span className="flex items-center gap-2 text-xs text-slate-400">
                  <Loader2 className="h-3 w-3 animate-spin" /> Generando el código…
                </span>
              )}
            </div>
          )}
        </div>
        <p className="text-center text-xs text-slate-400">
          {isConnected
            ? 'El canal quedó vinculado. Esta ventana se cierra sola.'
            : 'El código cambia cada pocos segundos; escanéalo en cuanto aparezca.'}
        </p>
      </div>
    </Modal>
  );
}
